import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useNavigationType } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { collection, doc, getDoc, setDoc, serverTimestamp, updateDoc, arrayUnion, addDoc, query, orderBy, onSnapshot, FieldPath } from 'firebase/firestore';
import { firestore } from './firebaseConfig';
import ChatWindow from './components/ChatWindow';
import './ChatWindowPage.scss';
import * as S from '../scss/theme';
import fnc from '../mymod/commFunction';
import { normalizeAcaCode, normalizeChatUserId } from '../mymod/normalizeAcaCode';

const ACTIVE_CHATROOM_STORAGE_KEY = 'activeChatroomId';

const toPlainUserId = (rawId) => {
    if (!rawId || typeof rawId !== 'string') {
        return '';
    }

    const trimmedId = rawId.trim();
    if (!trimmedId) {
        return '';
    }

    const tokens = trimmedId.split('_');
    if (tokens.length >= 2 && /^(?:[A-Za-z]+\d+|\d+)$/.test(tokens[0])) {
        return tokens.slice(1).join('_');
    }

    return trimmedId;
};
const ChatWindowPage = () => {
    const { chatroomId } = useParams();
    const navigate = useNavigate();
    const navigationType = useNavigationType();
    const userInfo = useSelector((state) => state.reduxStore.userInfo);
    const userInfo2 = useSelector((state) => state.reduxStore.userInfo2);
    const [chatroom, setChatroom] = useState(null);
    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [userProfile, setUserProfile] = useState(null);
    const messagesEndRef = useRef(null);
    
    // Firebase Auth 대신 사용자 ID 직접 사용
    const rawUserId = userInfo2?.id || userInfo?.id || '';
    const acaCode = normalizeAcaCode(userInfo2?.acaCode || userInfo?.acaCode || fnc.getCookie('acaCode') || fnc.getCookie('certCode'));
    const userId = normalizeChatUserId(rawUserId, acaCode);
    const plainUserId = toPlainUserId(rawUserId || userId);
    const userIdCandidates = [...new Set([userId, rawUserId, plainUserId].filter(Boolean))];
    const userIdCandidatesKey = userIdCandidates.join('|');

    // /chat 페이지를 브라우저 새로고침으로 직접 진입하면 로그인 화면으로 보냄
    useEffect(() => {
        if (typeof window === 'undefined' || typeof performance === 'undefined') {
            return;
        }

        const [navigationEntry] = performance.getEntriesByType('navigation');
        const isReload = navigationEntry?.type === 'reload';
        const isPopNavigation = navigationType === 'POP';

        if (isReload && isPopNavigation) {
            navigate('/login', { replace: true });
        }
    }, [navigate, navigationType]);

    // 현재 기기에서 실제로 열려 있는 채팅방을 표시한다.
    useEffect(() => {
        if (!chatroomId) return;

        try {
            sessionStorage.setItem(ACTIVE_CHATROOM_STORAGE_KEY, chatroomId);
        } catch (error) {
            // sessionStorage를 사용할 수 없어도 URL 확인으로 읽음 처리를 계속한다.
        }

        return () => {
            try {
                if (sessionStorage.getItem(ACTIVE_CHATROOM_STORAGE_KEY) === chatroomId) {
                    sessionStorage.removeItem(ACTIVE_CHATROOM_STORAGE_KEY);
                }
            } catch (error) {
                // 저장소 해제 실패는 화면 전환을 막지 않는다.
            }
        };
    }, [chatroomId]);

    // 사용자 프로필 로드 (Firebase Auth 제거)
    useEffect(() => {
        if (!userId) {
            setLoading(false);
            return;
        }

        const loadUserProfile = async () => {
            try {
                const userDocRef = doc(firestore, 'users', userId);
                const userDocSnap = await getDoc(userDocRef);
                
                let userNickname = userInfo2?.nickname || '사용자';
                if (userDocSnap.exists()) {
                    userNickname = userDocSnap.data().nickname || userNickname;
                }
                
                setUserProfile({
                    id: userId,
                    nickname: userNickname,
                    name: userNickname,
                    email: userInfo2?.email || userInfo?.email || '',
                    phoneNumber: userInfo2?.hp || userInfo?.hp || ''
                });
            } catch (error) {
                console.error('프로필 로드 오류:', error);
                setUserProfile({
                    id: userId,
                    nickname: userInfo2?.nickname || '사용자',
                    name: userInfo2?.nickname || '사용자',
                    email: userInfo2?.email || '',
                    phoneNumber: userInfo2?.hp || ''
                });
            }
        };

        loadUserProfile();
    }, [userId, userInfo2, navigate]);

    // 채팅방 정보 로드
    useEffect(() => {
        if (!chatroomId || !userId) return;

        const loadChatroom = async () => {
            try {
                const chatroomRef = doc(firestore, 'chatRooms', chatroomId);
                const chatroomSnap = await getDoc(chatroomRef);

                if (chatroomSnap.exists()) {
                    const chatroomData = chatroomSnap.data();
                    const participants = Array.isArray(chatroomData.participants) ? chatroomData.participants : [];
                    const isParticipant = participants.some((participantId) => userIdCandidates.includes(participantId));

                    if (participants.length > 0 && !isParticipant) {
                        console.warn('현재 로그인 사용자는 이 채팅방 참여자가 아닙니다.', { chatroomId, userId });
                        navigate('/talk', { replace: true });
                        return;
                    }

                    setChatroom({
                        id: chatroomSnap.id,
                        ...chatroomData
                    });

                    if (isParticipant && !participants.includes(userId)) {
                        await updateDoc(
                            chatroomRef,
                            'participants',
                            arrayUnion(userId),
                            new FieldPath('unreadCount', userId),
                            0
                        );
                    }
                } else {
                    console.error('채팅방을 찾을 수 없습니다');
                    navigate('/chatrooms');
                }
            } catch (error) {
                console.error('채팅방 로드 오류:', error);
            } finally {
                setLoading(false);
            }
        };

        loadChatroom();
    }, [chatroomId, userId, userIdCandidatesKey, navigate]);

    // 채팅방을 보고 있는 동안 늦게 반영되는 unreadCount도 읽음 처리
    useEffect(() => {
        if (!chatroomId || !userId || !firestore) return;

        const chatroomRef = doc(firestore, 'chatRooms', chatroomId);

        const clearUnreadBadge = async (chatroomData) => {
            if (typeof window !== 'undefined') {
                const activePath = window.location.pathname.replace(/\/+$/, '');
                const isCurrentChatroomPath = activePath === `/chat/${chatroomId}`
                    || activePath === `/chatWindowPage/${chatroomId}`;

                if (!isCurrentChatroomPath) {
                    return;
                }
            }

            try {
                if (sessionStorage.getItem(ACTIVE_CHATROOM_STORAGE_KEY) !== chatroomId) {
                    return;
                }
            } catch (error) {
                // sessionStorage를 사용할 수 없는 경우 URL 확인 결과만 사용한다.
            }

            const unreadCount = chatroomData?.unreadCount || {};
            const participants = Array.isArray(chatroomData?.participants)
                ? chatroomData.participants
                : [];
            const currentUserIds = userIdCandidates.filter((candidateId) => (
                participants.includes(candidateId) ||
                Object.prototype.hasOwnProperty.call(unreadCount, candidateId)
            ));
            const unreadUserIds = currentUserIds.filter(
                (candidateId) => Number(unreadCount[candidateId] || 0) > 0
            );

            if (unreadUserIds.length === 0) {
                return;
            }

            const updateArgs = [];
            unreadUserIds.forEach((candidateId) => {
                updateArgs.push(new FieldPath('unreadCount', candidateId), 0);
            });

            try {
                await updateDoc(chatroomRef, ...updateArgs);
            } catch (error) {
                console.error('읽음 배지 초기화 오류:', error);
            }
        };

        const unsubscribe = onSnapshot(chatroomRef, (snapshot) => {
            if (snapshot.exists()) {
                const chatroomData = snapshot.data();
                setChatroom((currentChatroom) => (
                    currentChatroom
                        ? { id: snapshot.id, ...chatroomData }
                        : currentChatroom
                ));
                clearUnreadBadge(chatroomData);
            }
        }, (error) => {
            console.error('읽음 배지 감시 오류:', error);
        });

        return () => {
            unsubscribe();
        };
    }, [chatroomId, userId, userIdCandidatesKey]);

    // 메시지 실시간 리스너 (Firestore)
    useEffect(() => {
        if (!chatroomId) return;

        const messagesRef = collection(firestore, `chatRooms/${chatroomId}/messages`);
        const q = query(messagesRef, orderBy('timestamp', 'asc'));
        
        const unsubscribe = onSnapshot(q, (snapshot) => {
            const messagesData = [];
            snapshot.forEach((doc) => {
                messagesData.push({
                    id: doc.id,
                    ...doc.data()
                });
            });
            
            setMessages(messagesData);
        });

        return () => {
            unsubscribe();
        };
    }, [chatroomId]);

    // 스크롤을 맨 아래로
    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    // 메시지 전송 (Firestore)
    const sendMessage = async (messageText, messageType = 'text', imageData = null) => {
        if (!chatroom || !userId) return;
        if (messageType === 'text' && !messageText.trim()) return;

        try {
            const messageData = {
                senderId: userId,
                senderName: userProfile?.nickname || userProfile?.name || '사용자',
                text: messageText, // 'message' 대신 'text' 사용
                messageType: messageType,
                timestamp: serverTimestamp(),
                chatroomId: chatroom.id
            };

            // 이미지 메시지인 경우 이미지 데이터 추가
            if (messageType === 'image' && imageData) {
                messageData.images = imageData;
            }

            // 파일 메시지인 경우 파일 데이터 추가
            if (messageType === 'file' && imageData) {
                messageData.fileData = imageData;
            }

            // Firestore chatRooms/{roomId}/messages 서브컬렉션에 메시지 저장
            const messagesRef = collection(firestore, `chatRooms/${chatroom.id}/messages`);
            await addDoc(messagesRef, messageData);
            
            // chatRooms 문서 업데이트 (unreadCount는 Cloud Function에서 한 번만 증가)
            const chatroomRef = doc(firestore, 'chatRooms', chatroom.id);
            const updateData = {
                lastMessage: messageText,
                lastMessageTime: serverTimestamp()
            };

            await updateDoc(chatroomRef, updateData);
            console.log('✅ 메시지 전송 완료');
        } catch (error) {
            console.error('❌ 메시지 전송 오류:', error);
            alert('메시지 전송에 실패했습니다: ' + error.message);
        }
    };

    // 참여자 추가
    const addParticipants = async (newParticipantIds) => {
        console.log('=== addParticipants (ChatWindowPage) 호출됨 ===');
        console.log('새 참여자들:', newParticipantIds);
        console.log('채팅방 ID:', chatroomId);
        
        if (!chatroomId || !newParticipantIds || newParticipantIds.length === 0) return;

        try {
                const chatroomRef = doc(firestore, 'chatRooms', chatroomId);
            
            // Firestore에서 participants 배열에 새 참여자 추가 및 unreadCount 초기화
            const updateData = {
                participants: arrayUnion(...newParticipantIds)
            };
            
            // 새 참여자들의 unreadCount를 0으로 초기화
            newParticipantIds.forEach(participantId => {
                updateData[`unreadCount.${participantId}`] = 0;
            });
            
            await updateDoc(chatroomRef, updateData);

            console.log(`✅ Firestore에 ${newParticipantIds.length}명 추가됨`);
            
            // 새로 추가된 참여자들에게 chatRoomId 저장 (MSSQL)
            console.log('chatRoomId를 MSSQL에 저장 시작...');
            for (const receiverId of newParticipantIds) {
                console.log(`→ ${receiverId}에게 chatRoomId 저장 시도...`);
                
                // fnc.sendAxios 사용으로 변경 (svrUrl이 자동으로 붙음)
                await new Promise((resolve) => {
                    fnc.sendAxios('/kuesbLoginRoutes/update/chatRoomId', {
                        senderId: userId,
                        receiverId: receiverId,
                        chatRoomId: chatroomId
                    }, (result) => {
                        console.log(`✅ chatRoomId 저장 완료 - 수신자: ${receiverId}`, result);
                        resolve(result);
                    });
                });
            }
            console.log('✅ 모든 참여자에게 chatRoomId 저장 완료');
            
            // 로컬 상태 업데이트
            setChatroom(prev => ({
                ...prev,
                participants: [...(prev.participants || []), ...newParticipantIds]
            }));

            alert(`${newParticipantIds.length}명이 채팅방에 추가되었습니다.`);
        } catch (error) {
            console.error('❌ 참여자 추가 오류:', error);
            alert('참여자 추가에 실패했습니다: ' + error.message);
        }
    };

    // 목록으로 돌아가기
    const backToList = () => {
        navigate('/chatrooms');
    };

    if (loading) {
        return (
            <div className="chat-window-loading">
                <div className="loading-spinner"></div>
                <p>로딩 중...</p>
            </div>
        );
    }

    if (!chatroom) {
        return (
            <div className="chat-window-error">
                <h3>채팅방을 찾을 수 없습니다</h3>
                <button onClick={backToList}>목록으로 돌아가기</button>
            </div>
        );
    }

    //==============================================================================
    return (
        <S.Section className="chat-window-section">
        <div className="ChatWindowPage">
            <ChatWindow
                chatroom={chatroom}
                messages={messages}
                currentUser={userProfile}
                userProfile={userProfile}
                onSendMessage={sendMessage}
                onAddParticipants={addParticipants}
                messagesEndRef={messagesEndRef}
                onBack={backToList}
            />
        </div>
        </S.Section>
    );
};

export default ChatWindowPage;
